import { useState, isValidElement } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { useModal } from '@belong/common';
import { Modal, Spinner, Tabs, Text } from '@belong/ui';
import type { GoogleAuthUnrelatedErrorResponse } from '@belong/ui';
import clsx from 'clsx';
import { RegisterUserModel } from 'src/api/models';
import { BenefitsAnimatedVerticalSlider } from 'src/features/auth/components/benefits-animated-vertical-slider/benefits-animated-vertical-slider';
import { ErrorMessage } from 'src/features/auth/components/error-message/error-message';
import { SignInTabPanel } from 'src/features/auth/components/sign-in-tab-panel/sign-in-tab-panel';
import { SignUpTabPanel } from 'src/features/auth/components/sign-up-tab-panel/sign-up-tab-panel';
import {
  EMAIL_TAKEN_API_CODE,
  TOKEN_EMAIL_MISMATCH,
} from 'src/features/auth/constants';
import { EnterContactModal } from 'src/features/auth/enter-contact-modal/enter-contact-modal';
import { AUTH_STRINGS } from 'src/features/auth/strings';
import type { LoginProviders } from 'src/features/auth/types';
import { isGoogleLoginError } from 'src/features/auth/utils';
import { ApiError } from 'src/utils/build-api-client';
import css from './sign-up-sign-in-modal.module.css';

type Props = {
  borderStyle?: 'solid' | 'shadow';
  header?: ReactNode;
  initialActiveTab?: 'sign-up' | 'sign-in';
  isOpen: ComponentProps<typeof Modal>['isOpen'];
  onDismiss: ComponentProps<typeof Modal>['onDismiss'];
  onSignInFinish?: ComponentProps<typeof SignInTabPanel>['onFinish'];
  onSignInSuccess?: ComponentProps<typeof SignInTabPanel>['onSignInSuccess'];
  onSignUpFinish?: ComponentProps<typeof SignUpTabPanel>['onFinish'];
  onSignUpSuccess?: ComponentProps<typeof SignUpTabPanel>['onSignUpSuccess'];
  overlayClassName?: ComponentProps<typeof Modal>['overlayClassName'];
  shouldAskForSignUpPassword?: boolean;
  shouldAskForSignUpSmsConsent?: boolean;
  size?: ComponentProps<typeof Modal>['size'];
  onOpenContactModalCallback?: () => void;
  enforcedEmail?: string;
};

export function SignUpSignInModal({
  borderStyle,
  header,
  initialActiveTab = 'sign-up',
  isOpen,
  onDismiss,
  onSignInFinish = async () => {},
  onSignInSuccess = async () => {},
  onSignUpFinish = async () => {},
  onSignUpSuccess = async () => {},
  overlayClassName,
  shouldAskForSignUpPassword = false,
  shouldAskForSignUpSmsConsent = false,
  size = 'fullScreen',
  onOpenContactModalCallback,
  enforcedEmail,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(
    initialActiveTab === 'sign-up' ? 0 : 1,
  );
  const [defaultEmailValue, setDefaultEmailValue] = useState<
    string | undefined
  >('');
  const [loginProvider, setLoginProvider] = useState<LoginProviders>('Apple');
  const [
    isEnterContactModalOpen,
    openEnterContactModal,
    closeEnterContactModal,
  ] = useModal();

  function handleStart() {
    setIsLoading(true);
    setError('');
  }

  async function handleSignUpFinish(hasError: boolean) {
    setIsLoading(false);

    await onSignUpFinish(hasError);
  }

  async function handleSignInFinish(hasError: boolean) {
    setIsLoading(false);

    await onSignInFinish(hasError);
  }

  async function handleExternalLoginSuccessWithoutPhoneOrEmail(
    selectedLoginProvider: LoginProviders,
  ) {
    setLoginProvider(selectedLoginProvider);

    openEnterContactModal();
  }

  function handleError(error: unknown, values?: RegisterUserModel) {
    let errorMessage = AUTH_STRINGS['error.generic'];

    if (error instanceof ApiError) {
      const { status, code } = error;

      if (status && status >= 400 && status < 500) {
        errorMessage = AUTH_STRINGS['sign.in.error.credentials'];

        if (code === EMAIL_TAKEN_API_CODE) {
          errorMessage = AUTH_STRINGS['error.email_taken'];

          setDefaultEmailValue(values?.email || '');
          setTabIndex(1);
        }

        if (code === TOKEN_EMAIL_MISMATCH) {
          errorMessage = AUTH_STRINGS['error.facebook'];
        }
      }
    }

    /**
     * When the user closes the popup, we don't want to show an error message.
     */
    if ((error as GoogleAuthUnrelatedErrorResponse).type === 'popup_closed') {
      return;
    }

    if (isGoogleLoginError(error) && error === 'INIT_FAILED') {
      errorMessage = AUTH_STRINGS['error.google'];
    }

    setError(errorMessage);
  }

  async function handleEnterContactModalSuccessfulLogin() {
    closeEnterContactModal();

    await onSignUpSuccess();
  }

  function handleTabsChange(index: number) {
    setTabIndex(index);
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        name="sign-up-sign-in-modal"
        onDismiss={onDismiss}
        size={size}
        overlayClassName={overlayClassName}
        borderStyle={borderStyle}
      >
        <div className="pt-2xl container flex w-full flex-col items-center sm:pt-0 md:h-full">
          {isValidElement(header) ? (
            header
          ) : (
            <>
              <div className="mb-sm">
                <Text
                  as="h1"
                  className={clsx('text-center', css.title)}
                  fontWeight="semibold"
                  variant="h2"
                >
                  {AUTH_STRINGS['sign.up.sign.in.title']}
                </Text>
              </div>
              <div className="mb-lg flex items-center">
                <BenefitsAnimatedVerticalSlider />
              </div>
            </>
          )}
          {error && (
            <ErrorMessage className={clsx('mb-sm', css.error)}>
              {error}
            </ErrorMessage>
          )}
          <div className="w-full">
            <Tabs
              defaultIndex={0}
              hasBlurOnScroll={false}
              index={tabIndex}
              onChange={handleTabsChange}
            >
              <div className={clsx('m-auto', css.tabList)}>
                <Tabs.TabList>
                  <Tabs.TabItem index={0}>
                    {AUTH_STRINGS['sign.up']}
                  </Tabs.TabItem>
                  <Tabs.TabItem index={1}>
                    {AUTH_STRINGS['sign.in']}
                  </Tabs.TabItem>
                </Tabs.TabList>
              </div>
              <Tabs.PanelList>
                <div
                  className={clsx(
                    'sm:flex sm:items-center sm:justify-center',
                    css.tabPanel,
                  )}
                >
                  <SignUpTabPanel
                    onError={handleError}
                    onExternalLoginSuccess={onSignUpSuccess}
                    onExternalLoginSuccessWithoutPhoneOrEmail={
                      handleExternalLoginSuccessWithoutPhoneOrEmail
                    }
                    onFinish={handleSignUpFinish}
                    onSignUpSuccess={onSignUpSuccess}
                    onStart={handleStart}
                    shouldAskForPassword={shouldAskForSignUpPassword}
                    shouldAskForSmsConsent={shouldAskForSignUpSmsConsent}
                    enforcedEmail={enforcedEmail}
                  />
                  <SignInTabPanel
                    onError={handleError}
                    onExternalLoginSuccess={onSignInSuccess}
                    onExternalLoginSuccessWithoutPhoneOrEmail={
                      handleExternalLoginSuccessWithoutPhoneOrEmail
                    }
                    onFinish={handleSignInFinish}
                    onSignInSuccess={onSignInSuccess}
                    onStart={handleStart}
                    defaultEmailValue={defaultEmailValue}
                    enforcedEmail={enforcedEmail}
                  />
                </div>
              </Tabs.PanelList>
            </Tabs>
          </div>
        </div>
        {isLoading && <Spinner />}
      </Modal>
      <EnterContactModal
        isOpen={isEnterContactModalOpen}
        onDismiss={closeEnterContactModal}
        onSuccessfulLogin={handleEnterContactModalSuccessfulLogin}
        overlayClassName="z-fullscreen"
        provider={loginProvider}
        shouldClearModals={false}
        onFinish={handleSignUpFinish}
        onOpenCallback={onOpenContactModalCallback}
      />
    </>
  );
}
